import {
	CreateDrawerFormProps,
	FormDrawerContentProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nTextField,
	UpdateDrawerFormProps,
	useDynamicConfig,
	validateEachLanguage,
} from "@dgs/core";
import { FormikErrors } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { organizationalUnitService } from "../shared/api/organizationalUnitService";
import { OrganizationalUnitFormValues, OrganizationalUnitTranslationEntry } from "../shared/types/organizationalUnit";
import { UserMultiSelectField } from "../users/UserSelectField";

const useValidate = () => {
	const { t } = useTranslation();

	return (values: OrganizationalUnitFormValues) => {
		const errors: Record<string, any> = {};
		return {
			...errors,
			...validateEachLanguage(values.translations, (_, translation) => {
				const errors: FormikErrors<OrganizationalUnitTranslationEntry> = {};
				if (!translation.name) {
					errors.name = t("Required field");
				}
				return errors;
			}),
		};
	};
};

const emptyTranslation = { name: "" };

const OrganizationalUnitFormDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();

	return (
		<I18nFormDrawerContent {...props} emptyTranslation={emptyTranslation}>
			<I18nTextField name="name" label={t("Name")} required="onlyOnMainForm" />
			<I18nOnlyOnMainForm>
				<UserMultiSelectField
					name="users"
					label={t("Approving users")}
					description={t("The users selected here can be chosen as approvers in the Event Hub.")}
				/>
			</I18nOnlyOnMainForm>
		</I18nFormDrawerContent>
	);
};

export const OrganizationalUnitCreateDrawer: FC<CreateDrawerFormProps<OrganizationalUnitFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const { languageSettings } = useDynamicConfig();

	const initialValues: OrganizationalUnitFormValues = useMemo(
		() => ({
			translations: [{ locale: languageSettings.fallback, name: "" }],
			users: [],
		}),
		[languageSettings.fallback],
	);

	return (
		<I18nFormDrawer
			{...props}
			initialValues={initialValues}
			title={t("Create organizational unit")}
			validate={validate}
			renderForm={(formProps) => <OrganizationalUnitFormDrawerContent {...formProps} {...props} />}
		/>
	);
};

export const OrganizationalUnitUpdateDrawer: FC<UpdateDrawerFormProps<OrganizationalUnitFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<I18nFormFetchDrawer
			{...props}
			title={t("Edit organizational unit")}
			queryFn={() => organizationalUnitService.show(props.id).then((x) => x.data.data)}
			queryKey={organizationalUnitService.showKey(props.id)}
			validate={validate}
			renderSuccess={(formProps) => <OrganizationalUnitFormDrawerContent {...props} {...formProps} />}
		/>
	);
};
